import type {
  ApiProblemDetails,
  ErrorResponse,
  FetchArgsOptions,
  FetchResponse,
  FetchResponseOfError,
} from "./Api";

type BaseResponse<TData = unknown> =
  | FetchResponse<TData, number>
  | ErrorResponse;

type SuccessResponse<TResponse extends BaseResponse> = Extract<
  TResponse,
  { status: 200 | 201 | 202 | 204 }
>;

type SuccessResponseData<TResponse extends BaseResponse> =
  SuccessResponse<TResponse>["data"];

export class ApiCallError extends Error {
  data: FetchResponseOfError;

  constructor(data: FetchResponseOfError) {
    super();
    this.data = data;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiCallError {
    return obj?.isApiException === true;
  }
}

/**
 * A raw error response thrown by the generated client. `args` is required alongside `status`:
 * third-party SDK errors (e.g. stream-chat's ErrorFromResponse) also carry a numeric `status`
 * and must not be mistaken for our API responses.
 */
export const isApiErrorResponse = (
  error: unknown,
): error is FetchResponseOfError =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  typeof error.status === "number" &&
  "args" in error;

export const getApiErrorResponse = (
  error: unknown,
): FetchResponseOfError | undefined => {
  if (ApiCallError.isApiException(error)) {
    return error.data;
  }

  if (isApiErrorResponse(error)) {
    return error;
  }

  return undefined;
};

export const getApiErrorStatus = (error: unknown): number | undefined =>
  getApiErrorResponse(error)?.status;

/**
 * Whether a failed API call is worth retrying: server-side failures (5xx) and network-level
 * failures (status 0). Everything else — 4xx outcomes and non-API throws — will not change on a
 * retry. Shared by the query client's default retry predicate and hooks with their own retry.
 */
export const isRetryableApiError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);

  return status !== undefined && (status >= 500 || status === 0);
};

const isApiProblemDetails = (data: unknown): data is ApiProblemDetails =>
  typeof data === "object" &&
  data !== null &&
  "errors" in data &&
  typeof data.errors === "object" &&
  data.errors !== null;

/**
 * The ApiProblemDetails error dictionary of a thrown error response, keyed by the Result.Fail
 * name (e.g. "BackOfficeTarget", "PhotoOutside"), or undefined when the response carried none
 * (network failure, ingress error page, a non-API throw).
 */
export const getApiProblemErrors = (
  error: unknown,
): ApiProblemDetails["errors"] | undefined => {
  // Normalize first: the details sit one level deeper on an ApiCallError than on a
  // response thrown straight from processResponseWithApiProblemDetails. Widened on
  // purpose: the generated error type declares `data: null`, but a 4xx body carries
  // the ApiProblemDetails the guard looks for.
  const data: unknown = getApiErrorResponse(error)?.data;

  return isApiProblemDetails(data) ? data.errors : undefined;
};

/**
 * True when a thrown error response carries the given ApiProblemDetails error key — the backend
 * keys precondition failures by the Result.Fail name (e.g. "BackOfficeTarget"), which is stable
 * across status codes and languages, unlike the message text.
 */
export const hasApiProblemError = (
  error: unknown,
  errorKey: string,
): boolean => {
  const errors = getApiProblemErrors(error);

  return errors !== undefined && errorKey in errors;
};

/**
 * Process response and throw error if status code is not 2xx
 * @param request request function
 * @returns Processed response
 */
export const processResponseWithData =
  <T>(
    request: () => Promise<FetchResponse<{ Data: T }, 200> | ErrorResponse>,
  ) =>
  async () => {
    const response = await request();
    return processResponseStatusCode(response)?.Data;
  };

function processResponseStatusCode<TResponse extends BaseResponse>(
  response: TResponse,
): SuccessResponseData<TResponse> {
  if (response?.status >= 400 || response?.status === 0) {
    throw new ApiCallError(response as FetchResponseOfError);
  }

  return response?.data;
}

/**
 *  Process response and throw ApiProblemDetails error if status code is not 2xx
 * @param request request function
 * @returns Processed response
 */
export const processResponseWithApiProblemDetails =
  <TArgs = void, TResponse extends BaseResponse = BaseResponse>(
    request: (args: TArgs, options?: FetchArgsOptions) => Promise<TResponse>,
  ) =>
  async (args: TArgs): Promise<SuccessResponseData<TResponse>> => {
    const response = await request(args);
    return processResponseStatusCodeWithApiProblemDetails<TResponse>(response);
  };

function processResponseStatusCodeWithApiProblemDetails<
  TResponse extends BaseResponse,
>(response: TResponse): SuccessResponseData<TResponse> {
  if (response?.status >= 400 || response?.status === 0) {
    throw response;
  }

  return response?.data;
}
